import type {
  Country,
  EnumDefinition,
  EnumItemBase,
  EnumValues,
} from "@dfp/isomorphic-enums";

import { projectName } from "../projectName.ts";

export interface BankEnumItem extends EnumItemBase {
  name: string;
  countryCode: Country;
  bics: string[];
  bankCodes: string[];
}

export type Bank = EnumValues<typeof bankEnum>;

export const bankEnum = {
  header: {
    businessName: "Bank",
    businessOwner: "Core team",
    dataSource: "Internal",
    description: "Supported banks",
    scopeOfUsage: "Shared",
    technicalName: "bank",
    projectName,
    additionalAttributes: {
      name: {
        dataSource: "Internal",
        description: "Name of bank.",
      },
      countryCode: { dataSource: "Internal", description: "" },
      bics: { dataSource: "Internal", description: "" },
      bankCodes: { dataSource: "Internal", description: "" },
    },
  },
  values: [
    /* ---------------------------------
     * CZE
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "AGBACZPPXXX",
      name: "Moneta Money Bank",
      countryCode: "CZE",
      bics: ["AGBACZPP", "AGBACZPPXXX"],
      bankCodes: ["0600"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "FIOBCZPPXXX",
      name: "Fio banka",
      countryCode: "CZE",
      bics: ["FIOBCZPP", "FIOBCZPPXXX"],
      bankCodes: ["2010"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CTASCZ22XXX",
      name: "Banka CREDITAS",
      countryCode: "CZE",
      bics: ["CTASCZ22", "CTASCZ22XXX"],
      bankCodes: ["2250"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BREXCZPPXXX",
      name: "mBank",
      countryCode: "CZE",
      bics: ["BREXCZPP", "BREXCZPPXXX"],
      bankCodes: ["6210"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "RZBCCZPPXXX",
      name: "Raiffeisen",
      countryCode: "CZE",
      bics: ["RZBCCZPP", "RZBCCZPPXXX"],
      bankCodes: ["5500"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BACXCZPPXXX",
      name: "UniCredit Bank",
      countryCode: "CZE",
      bics: ["BACXCZPP", "BACXCZPPXXX"],
      bankCodes: ["2700"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "GIBACZPXXXX",
      name: "Česká spořitelna",
      countryCode: "CZE",
      bics: ["GIBACZPX", "GIBACZPXXXX"],
      bankCodes: ["0800"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "JTBPCZPPXXX",
      name: "J&T Banka",
      countryCode: "CZE",
      bics: ["JTBPCZPP", "JTBPCZPPXXX"],
      bankCodes: ["5800"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "AIRACZPPXXX",
      name: "Air Bank",
      countryCode: "CZE",
      bics: ["AIRACZPP", "AIRACZPPXXX"],
      bankCodes: ["3030"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CEKOCZPPXXX",
      name: "Československá obchodní banka",
      countryCode: "CZE",
      bics: ["CEKOCZPP", "CEKOCZPPXXX"],
      bankCodes: ["0300"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "KOMBCZPPXXX",
      name: "Komerční banka",
      countryCode: "CZE",
      bics: ["KOMBCZPP", "KOMBCZPPXXX"],
      bankCodes: ["0100"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "MIDLCZPPXXX",
      name: "HSBC Continental Europe, Czech Republic",
      countryCode: "CZE",
      bics: ["MIDLCZPP", "MIDLCZPPXXX"],
      bankCodes: ["8150"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "INGBCZPPXXX",
      name: "ING Bank N.V.",
      countryCode: "CZE",
      bics: ["INGBCZPP", "INGBCZPPXXX"],
      bankCodes: ["3500"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "OBKLCZ2XXXX",
      name: "Oberbank AG, Czech Republic",
      countryCode: "CZE",
      bics: ["OBKLCZ2X", "OBKLCZ2XXXX"],
      bankCodes: ["8040"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "GEBACZPPXXX",
      name: "BNP Paribas S.A., Czech Republic",
      countryCode: "CZE",
      bics: ["GEBACZPP", "GEBACZPPXXX"],
      bankCodes: ["6300"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCKBANKXXX",
      name: "Mock Bank",
      countryCode: "CZE",
      bics: ["MOCKBANK", "MOCKBANKXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "MPUBCZPPXXX",
      name: "Trinity Bank",
      countryCode: "CZE",
      bics: ["MPUBCZPP", "MPUBCZPPXXX"],
      bankCodes: ["2070"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CNBACZPPXXX",
      name: "Česká národní banka",
      countryCode: "CZE",
      bics: ["CNBACZPP", "CNBACZPPXXX"],
      bankCodes: ["0710"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCKBANK2XX",
      name: "Mock Bank 2",
      countryCode: "CZE",
      bics: [],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCKFAKEXXX",
      name: "Fake Bank (Salt Edge)",
      countryCode: "CZE",
      bics: [],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCKFSIMXXX",
      name: "Fake Bank Simple (Salt Edge)",
      countryCode: "CZE",
      bics: [],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCKOAUTXXX",
      name: "Fake Bank OAuth (Salt Edge)",
      countryCode: "CZE",
      bics: [],
      bankCodes: [],
    },
    /* ---------------------------------
     * SVK
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "BREXSKBX",
      name: "mBank",
      countryCode: "SVK",
      bics: ["BREXSKBX", "BREXSKBXXXX"],
      bankCodes: ["8360"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "GIBASKBX",
      name: "Slovenská sporiteľňa",
      countryCode: "SVK",
      bics: ["GIBASKBX", "GIBASKBXXXX"],
      bankCodes: ["0900"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CEKOSKBX",
      name: "ČSOB",
      countryCode: "SVK",
      bics: ["CEKOSKBX", "CEKOSKBXXXX"],
      bankCodes: ["7500"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "TATRSKBX",
      name: "Tatra Banka",
      countryCode: "SVK",
      bics: ["TATRSKBX", "TATRSKBXXXX"],
      bankCodes: ["1100"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "UNCRSKBX",
      name: "UniCredit Bank",
      countryCode: "SVK",
      bics: ["UNCRSKBX", "UNCRSKBXXXX"],
      bankCodes: ["1111"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "KOMASK2X",
      name: "Prima banka",
      countryCode: "SVK",
      bics: ["KOMASK2X", "KOMASK2XXXX"],
      bankCodes: ["5600"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "FIOZSKBA",
      name: "Fio banka",
      countryCode: "SVK",
      bics: ["FIOZSKBA", "FIOZSKBAXXX"],
      bankCodes: ["8330"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "KOMBSKBA",
      name: "Komerční banka",
      countryCode: "SVK",
      bics: ["KOMBSKBA", "KOMBSKBAXXX"],
      bankCodes: ["8100"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "SUBASKBX",
      name: "VUB",
      countryCode: "SVK",
      bics: ["SUBASKBXXXX", "SUBASKBX"],
      bankCodes: ["0200"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "POBNSKBA",
      name: "365 Bank",
      countryCode: "SVK",
      bics: ["POBNSKBA", "POBNSKBAXXX"],
      bankCodes: ["6500"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "NBSBSKBX",
      name: "Národná banka Slovenska",
      countryCode: "SVK",
      bics: ["NBSBSKBX", "NBSBSKBXXXX"],
      bankCodes: ["0720"],
    },
    /* ---------------------------------
     * POL
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "BREXPLPW",
      name: "mBank",
      countryCode: "POL",
      bics: ["BREXPLPW", "BREXPLPWXXX"],
      bankCodes: ["114"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "ALBPPLPW",
      name: "Alior Bank",
      countryCode: "POL",
      bics: ["ALBPPLPWXXX", "ALBPPLP1BMW", "ALBPPLPW"],
      bankCodes: ["249"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "PKOPPLPW",
      name: "Bank Pekao",
      countryCode: "POL",
      bics: ["PKOPPLPWXXX", "PKOPPLPW"],
      bankCodes: ["124", "195"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "PPABPLPK",
      name: "BNP Paribas",
      countryCode: "POL",
      bics: ["PPABPLPK", "PPABPLPKXXX"],
      bankCodes: ["160", "203", "175", "234", "227"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BPKOPLPW",
      name: "PKO Bank Polski",
      countryCode: "POL",
      bics: ["BPKOPLPW", "BPKOPLPWXXX"],
      bankCodes: ["102", "144"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CITIPLPX",
      name: "Citi Handlowy",
      countryCode: "POL",
      bics: ["CITIPLPX", "CITIPLPXXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BIGBPLPW",
      name: "Millennium Bank",
      countryCode: "POL",
      bics: ["BIGBPLPW", "BIGBPLPWXXX"],
      bankCodes: ["116"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "WBKPPLPP",
      name: "Erste Bank Polska",
      countryCode: "POL",
      bics: ["WBKPPLPP", "WBKPPLPPXXX"],
      bankCodes: ["109", "150"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "INGBPLPW",
      name: "ING",
      countryCode: "POL",
      bics: ["INGBPLPW", "INGBPLPWXXX"],
      bankCodes: [],
    },
    /* ---------------------------------
     * DEU
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "DEUTDEFF",
      name: "Deutsche Bank",
      countryCode: "DEU",
      bics: ["DEUTDEFF", "DEUTDEFFXXX"],
      bankCodes: ["10070000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "COBADEFF",
      name: "Commerzbank",
      countryCode: "DEU",
      bics: ["COBADEFF", "COBADEFFXXX"],
      bankCodes: ["10040000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "FFVBDEFF",
      name: "Frankfurter Volksbank RHEIN-MAIN eG",
      countryCode: "DEU",
      bics: ["FFVBDEFF", "FFVBDEFFXXX", "GENODEF1ALZ", "GENODEF1AB1"],
      bankCodes: ["50190000", "79567531", "79562514"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HYVEDEMM",
      name: "Hypovereinsbank",
      countryCode: "DEU",
      bics: ["HYVEDEMM", "HYVEDEMMXXX"],
      bankCodes: ["70020270"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "PBNKDEFF",
      name: "Postbank",
      countryCode: "DEU",
      bics: ["PBNKDEFF", "PBNKDEFFXXX"],
      bankCodes: ["10010010"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "INGDDEFF",
      name: "ING",
      countryCode: "DEU",
      bics: ["INGDDEFF", "INGDDEFFXXX"],
      bankCodes: ["50010517", "50021000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "SOLADEST",
      name: "LBBW",
      countryCode: "DEU",
      bics: ["SOLADEST", "SOLADESTXXX"],
      bankCodes: ["60050101"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "QNTODEB2",
      name: "Qonto",
      countryCode: "DEU",
      bics: ["QNTODEB2", "QNTODEB2XXX"],
      bankCodes: ["10010123"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "NTSBDEB1XXX",
      name: "N26",
      countryCode: "DEU",
      bics: ["NTSBDEB1", "NTSBDEB1XXX", "NTSBDEBB", "NTSBDEBBXXX"],
      bankCodes: ["10011001"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HOLVDEB1XXX",
      name: "Holvi",
      countryCode: "DEU",
      bics: ["HOLVDEB1", "HOLVDEB1XXX"],
      bankCodes: ["10017997"],
    },
    {
      valueUsage: "CanBeEntered",
      // WELADED1 is shared across Sparkassen; only the branch BIC identifies this one.
      value: "WELADED1GRL",
      name: "SPK Oberlausitz-Niederschlesien",
      countryCode: "DEU",
      bics: ["WELADED1GRL"],
      bankCodes: ["85050100"],
    },
    /* ---------------------------------
     * AUT
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "BTVAAT22XXX",
      name: "BTV Vier Länder Bank AG",
      countryCode: "AUT",
      bics: ["BTVAAT22", "BTVAAT22XXX"],
      bankCodes: ["16000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "OBKLAT2LXXX",
      name: "Oberbank",
      countryCode: "AUT",
      bics: ["OBKLAT2L", "OBKLAT2LXXX"],
      bankCodes: ["15000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BFKKAT2KXXX",
      name: "BKS Bank",
      countryCode: "AUT",
      bics: ["BFKKAT2K", "BFKKAT2KXXX"],
      bankCodes: ["17000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HSEEAT2KXXX",
      name: "Addiko Bank",
      countryCode: "AUT",
      bics: ["HSEEAT2K", "HSEEAT2KXXX"],
      bankCodes: ["52300"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HAABAT2KXXX",
      name: "Austrian Anadi Bank",
      countryCode: "AUT",
      bics: ["HAABAT2K", "HAABAT2KXXX"],
      bankCodes: ["52000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BAWAATWWXXX",
      name: "BAWAG P.S.K.",
      countryCode: "AUT",
      bics: ["BAWAATWW", "BAWAATWWXXX"],
      bankCodes: ["14000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "EASYATW1XXX",
      name: "Easybank",
      countryCode: "AUT",
      bics: ["EASYATW1", "EASYATW1XXX"],
      bankCodes: ["14200"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "SPBAATWWXXX",
      name: "Bank 99",
      countryCode: "AUT",
      bics: ["SPBAATWW", "SPBAATWWXXX"],
      bankCodes: ["19120"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "GIBAATWWXXX",
      name: "Erste Bank and Sparkassen",
      countryCode: "AUT",
      bics: ["GIBAATWW", "GIBAATWWXXX", "GIBAAT21", "GIBAAT21XXX"],
      bankCodes: ["20111", "20100"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BGENATWWXXX",
      name: "Generali Bank",
      countryCode: "AUT",
      bics: ["BGENATWW", "BGENATWWXXX"],
      bankCodes: ["18400"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HYPNATWWXXX",
      name: "Hypo Noe",
      countryCode: "AUT",
      bics: ["HYPNATWWXXX", "HYPNATWW"],
      bankCodes: ["53000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HYPTAT22XXX",
      name: "Hypo Tirol",
      countryCode: "AUT",
      bics: ["HYPTAT22", "HYPTAT22XXX"],
      bankCodes: ["57000", "57100"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HYPVAT2BXXX",
      name: "Hypo Vorarlberg",
      countryCode: "AUT",
      bics: ["HYPVAT2B", "HYPVAT2BXXX"],
      bankCodes: ["58000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "RZBAATWWXXX",
      name: "Raiffeisen",
      countryCode: "AUT",
      bics: ["RZBAATWW", "RZBAATWWXXX", "RLNWATWW", "RLNWATWWXXX"],
      bankCodes: ["32000", "35000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BKAUATWWXXX",
      name: "Bank Austria Unicredit",
      countryCode: "AUT",
      bics: ["BKAUATWW", "BKAUATWWXXX"],
      bankCodes: ["11000", "12000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "VRBKAT21XXX",
      name: "VR-Bank Rottal-Inn eG",
      countryCode: "AUT",
      bics: ["VRBKAT21XXX", "VRBKAT21"],
      bankCodes: ["49500"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "VBOEATWWNOM",
      name: "Volksbank Niederösterreich",
      countryCode: "AUT",
      bics: ["VBOEATWWNOM"],
      bankCodes: ["47150"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "VBOEATWWOOE",
      name: "Volksbank Oberösterreich",
      countryCode: "AUT",
      bics: ["VBOEATWWOOE"],
      bankCodes: ["44800"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "VBOEATWWSAL",
      name: "Volksbank Salzburg",
      countryCode: "AUT",
      bics: ["VBOEATWWSAL"],
      bankCodes: ["45010"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "VBOEATWWGRA",
      name: "Volksbank Steiermark",
      countryCode: "AUT",
      bics: ["VBOEATWWGRA"],
      bankCodes: ["44770"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "VBOEATWWINN",
      name: "Volksbank Tirol",
      countryCode: "AUT",
      bics: ["VBOEATWWINN"],
      bankCodes: ["42390"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "VOVBAT2BXXX",
      name: "Volksbank Vorarlberg",
      countryCode: "AUT",
      bics: ["VOVBAT2B", "VOVBAT2BXXX"],
      bankCodes: ["45710"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "VBOEATWWXXX",
      name: "Volksbank Wien",
      countryCode: "AUT",
      bics: ["VBOEATWW", "VBOEATWWXXX"],
      bankCodes: ["43000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "VBOEATWWKLA",
      name: "Volksbank Kaernten",
      countryCode: "AUT",
      bics: ["VBOEATWWKLA"],
      bankCodes: ["42130"],
    },
    /* ---------------------------------
     * LTU
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "REVOLT21XXX",
      name: "Revolut",
      countryCode: "LTU",
      bics: ["RETBLT21", "REVOLT21", "RVUALT2V", "REVOLT21XXX", "RETBLT21XXX"],
      bankCodes: ["32500"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HABALT22XXX",
      name: "Swedbank",
      countryCode: "LTU",
      bics: ["HABALT22", "HABALT22XXX"],
      bankCodes: ["73000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CBVILT2XXXX",
      name: "SEB",
      countryCode: "LTU",
      bics: ["CBVILT2X", "CBVILT2XXXX"],
      bankCodes: ["70440"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CBSBLT26XXX",
      name: "Artea",
      countryCode: "LTU",
      bics: ["CBSBLT26", "CBSBLT26XXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "AGBLLT2XXXX",
      name: "Luminor",
      countryCode: "LTU",
      bics: ["AGBLLT2X", "AGBLLT2XXXX"],
      bankCodes: ["40100", "21400"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "INDULT2XXXX",
      name: "Citadele Bank",
      countryCode: "LTU",
      bics: ["INDULT2X", "INDULT2XXXX"],
      bankCodes: ["72900"],
    },
    /* ---------------------------------
     * BEL
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "TRWIBEBB",
      name: "Wise",
      countryCode: "BEL",
      bics: ["TRWIBEBB", "TRWIBEBBXXX", "TRWIBEB3XXX", "TRWIBEB1XXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "GEBABEBBXXX",
      name: "BNP Paribas Fortis",
      countryCode: "BEL",
      bics: ["GEBABEBB", "GEBABEBBXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "KREDBEBBXXX",
      name: "KBC",
      countryCode: "BEL",
      bics: ["KREDBEBBXXX", "KREDBEBB"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "GKCCBEBBXXX",
      name: "Belfius",
      countryCode: "BEL",
      bics: ["GKCCBEBBXXX", "GKCCBEBB"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BBRUBEBBXXX",
      name: "ING",
      countryCode: "BEL",
      bics: ["BBRUBEBB", "BBRUBEBBXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "ARSPBE22XXX",
      name: "Argenta",
      countryCode: "BEL",
      bics: ["ARSPBE22XXX", "ARSPBE22"],
      bankCodes: ["979", "978", "980"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "NICABEBBXXX",
      name: "Crelan",
      countryCode: "BEL",
      bics: ["NICABEBBXXX", "NICABEBB"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "TRIOBEBBTG2",
      name: "Triodos",
      countryCode: "BEL",
      bics: ["TRIOBEBBTG2", "TRIOBEBB", "TRIOBEBBXXX"],
      bankCodes: ["523"],
    },
    /* ---------------------------------
     * LUX
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "PPLXLUL2",
      name: "PayPal",
      countryCode: "LUX",
      bics: ["PPLXLUL2", "PPLXLUL2XXX"],
      bankCodes: [],
    },
    /* ---------------------------------
     * MUS
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "MOCKALABXXX",
      name: "Mock Allatra Bank",
      countryCode: "MUS",
      bics: [],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCKCITBXXX",
      name: "Mock Citrix Bank",
      countryCode: "MUS",
      bics: [],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCKBUNBXXX",
      name: "Mock Bunbonambo Bank",
      countryCode: "MUS",
      bics: [],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "MOCKSEPBXXX",
      name: "Mock Sepultura Bank",
      countryCode: "MUS",
      bics: [],
      bankCodes: [],
    },
    /* ---------------------------------
     * BGR
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "UNCRBGSFXXX",
      name: "UniCredit Bulbank",
      countryCode: "BGR",
      bics: [
        "UNCRBGSF",
        "UNCRBGSF031",
        "UNCRBGSF081",
        "UNCRBGSF426",
        "UNCRBGSF720",
        "UNCRBGSF733",
        "UNCRBGSFXXX",
      ],
      bankCodes: ["7000", "7630", "7527"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "STSABGSFXXX",
      name: "DSK Bank",
      countryCode: "BGR",
      bics: ["STSABGSF", "STSABGSFXXX"],
      bankCodes: ["9300"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "UBBSBGSFXXX",
      name: "UBB",
      countryCode: "BGR",
      bics: ["UBBSBGSF", "UBBSBGSFXXX"],
      bankCodes: ["8002", "8888", "8155", "7827"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BPBIBGSFXXX",
      name: "Пощенска Банка",
      countryCode: "BGR",
      bics: ["BPBIBGSF", "BPBIBGSFXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "FINVBGSFXXX",
      name: "Fibank",
      countryCode: "BGR",
      bics: ["FINVBGSF", "FINVBGSFXXX"],
      bankCodes: ["9150"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CECBBGSFXXX",
      name: "Centralna Kooperativna Banka",
      countryCode: "BGR",
      bics: ["CECBBGSF", "CECBBGSFXXX"],
      bankCodes: ["9790"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "PRCBBGSFXXX",
      name: "ProCredit Bank",
      countryCode: "BGR",
      bics: ["PRCBBGSFXXX", "PRCBBGSF"],
      bankCodes: ["9230"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "TBIBBGSFXXX",
      name: "TBIBank",
      countryCode: "BGR",
      bics: ["TBIBBGSF", "TBIBBGSFXXX"],
      bankCodes: ["9310"],
    },
    /* ---------------------------------
     * HRV
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "ZABAHR2XXXX",
      name: "Zagrebacka banka d.d.",
      countryCode: "HRV",
      bics: ["ZABAHR2X", "ZABAHR2XXXX"],
      bankCodes: ["2360000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "PBZGHR2XXXX",
      name: "Privredna Banka Zagreb",
      countryCode: "HRV",
      bics: ["PBZGHR2XXXX", "PBZGHR2X"],
      bankCodes: ["2340009"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "ESBCHR22XXX",
      name: "Erste&Steiermärkische Bank d.d.",
      countryCode: "HRV",
      bics: ["ESBCHR22XXX", "ESBCHR22"],
      bankCodes: ["2402006"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "OTPBHR2XXXX",
      name: "OTP banka",
      countryCode: "HRV",
      bics: ["OTPBHR2X", "OTPBHR2XXXX"],
      bankCodes: ["2407000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "RZBHHR2XXXX",
      name: "Raiffeisen",
      countryCode: "HRV",
      bics: ["RZBHHR2X", "RZBHHR2XXXX"],
      bankCodes: ["2484008"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HPBZHR2XXXX",
      name: "HPB",
      countryCode: "HRV",
      bics: ["HPBZHR2X", "HPBZHR2XXXX"],
      bankCodes: ["2390001"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "KREZHR2XXXX",
      name: "Agram Banka",
      countryCode: "HRV",
      bics: ["KREZHR2X", "KREZHR2XXXX"],
      bankCodes: ["2481000"],
    },
    /* ---------------------------------
     * DNK
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "DABADKKKXXX",
      name: "Danske Bank",
      countryCode: "DNK",
      bics: ["DABADKKKXXX", "DABADKKK"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "NDEADKKKXXX",
      name: "Nordea",
      countryCode: "DNK",
      bics: ["NDEADKKK", "NDEADKKKXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "LOSADKKKXXX",
      name: "Lån & Spar Bank",
      countryCode: "DNK",
      bics: ["LOSADKKK", "LOSADKKKXXX"],
      bankCodes: ["0400"],
    },
    /* ---------------------------------
     * EST
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "HABAEE2XXXX",
      name: "Swedbank",
      countryCode: "EST",
      bics: ["HABAEE2X", "HABAEE2XXXX"],
      bankCodes: ["22"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "EEUHEE2XXXX",
      name: "SEB",
      countryCode: "EST",
      bics: ["EEUHEE2X", "EEUHEE2XXXX"],
      bankCodes: ["10"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "LHVBEE22XXX",
      name: "LHV",
      countryCode: "EST",
      bics: ["LHVBEE22", "LHVBEE22XXX"],
      bankCodes: ["77"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "RIKOEE22XXX",
      name: "Luminor",
      countryCode: "EST",
      bics: ["RIKOEE22", "RIKOEE22XXX"],
      bankCodes: ["96", "17"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "EKRDEE22XXX",
      name: "Coop Pank",
      countryCode: "EST",
      bics: ["EKRDEE22", "EKRDEE22XXX"],
      bankCodes: ["42"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "PARXEE22XXX",
      name: "Citadele Bank",
      countryCode: "EST",
      bics: ["PARXEE22", "PARXEE22XXX"],
      bankCodes: ["12"],
    },
    /* ---------------------------------
     * FIN
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "OKOYFIHHXXX",
      name: "OP",
      countryCode: "FIN",
      bics: ["OKOYFIHH", "OKOYFIHHXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "NDEAFIHHXXX",
      name: "Nordea",
      countryCode: "FIN",
      bics: ["NDEAFIHH", "NDEAFIHHXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "DABAFIHHXXX",
      name: "Danske Bank",
      countryCode: "FIN",
      bics: ["DABAFIHH", "DABAFIHHXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "SBANFIHHXXX",
      name: "S-Pankki",
      countryCode: "FIN",
      bics: ["SBANFIHHXXX", "SBANFIHH"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "ITELFIHHXXX",
      name: "Säästöpankki",
      countryCode: "FIN",
      bics: ["ITELFIHH", "ITELFIHHXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "OMSAFI2SXXX",
      name: "Oma Säästöpankki",
      countryCode: "FIN",
      bics: ["OMSAFI2S", "OMSAFI2SXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HELSFIHHXXX",
      name: "Aktia",
      countryCode: "FIN",
      bics: ["HELSFIHH", "HELSFIHHXXX"],
      bankCodes: ["405550", "405500", "405590"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "POPFFI22XXX",
      name: "POP Pankki",
      countryCode: "FIN",
      bics: ["POPFFI22", "POPFFI22XXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "AABAFI22XXX",
      name: "Ålandsbanken",
      countryCode: "FIN",
      bics: ["AABAFI22", "AABAFI22XXX"],
      bankCodes: ["660100"],
    },
    /* ---------------------------------
     * FRA
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "BNPAFRPPXXX",
      name: "Connexis Cash (BNP Paribas)",
      countryCode: "FRA",
      bics: [
        "BNPAFRPPXXX",
        "BNPAFRPP",
        "FTSBSESSXXX",
        "BNPAGB22XXX",
        "BNPAPTPLXXX",
        "BNPAIE2DXXX",
        "FTSBSESS",
        "BNPAGB22",
        "BNPAPTPL",
        "BNPAIE2D",
      ],
      bankCodes: ["30004"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "SOGTFRP1XXX",
      name: "Société Générale (Particuliers)",
      countryCode: "FRA",
      bics: [
        "SOGTFRP1XXX",
        "SOGTFRP1",
        "SOGEFRPP",
        "NORDFRPP",
        "TARNFR2LXXX",
        "COURFR2TXXX",
        "BNUGFR21",
        "RALPFR2GXXX",
        "KOLBFR21",
        "LAYDFR2WXXX",
        "SDBMMCM2XXX",
        "SMCTFR2AXXX",
        "SOGEFRPPXXX",
        "NORDFRPPXXX",
        "TARNFR2L",
        "COURFR2T",
        "BNUGFR21XXX",
        "RALPFR2G",
        "KOLBFR21XXX",
        "LAYDFR2W",
        "SDBMMCM2",
        "SMCTFR2A",
      ],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CMCIFRCPCIC",
      name: "CIC",
      countryCode: "FRA",
      bics: ["CMCIFRCPCIC", "CMCIFRCP", "CMCIFRCPXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CRLYFRPPXXX",
      name: "LCL",
      countryCode: "FRA",
      bics: ["CRLYFRPP", "CRLYFRPPXXX"],
      bankCodes: ["30002"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "QNTOFRP1XXX",
      name: "Qonto",
      countryCode: "FRA",
      bics: ["QNTOFRP1", "QNTOFRP1XXX"],
      bankCodes: ["16958"],
    },
    /* ---------------------------------
     * GRC
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "ETHNGRAAXXX",
      name: "National Bank of Greece",
      countryCode: "GRC",
      bics: ["ETHNGRAA", "ETHNGRAAXXX"],
      bankCodes: ["011"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "PIRBGRAAXXX",
      name: "Piraeus Bank",
      countryCode: "GRC",
      bics: ["PIRBGRAA", "PIRBGRAAXXX"],
      bankCodes: ["017"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "ERBKGRAAXXX",
      name: "Eurobank",
      countryCode: "GRC",
      bics: [
        "ERBKGRAA",
        "ERBKGRAAXXX",
        "EFGBGRAASEC",
        "EFGBGRAA",
        "EFGBGRAAXXX",
      ],
      bankCodes: ["026"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CRBAGRAAXXX",
      name: "Alpha Bank",
      countryCode: "GRC",
      bics: ["CRBAGRAA", "CRBAGRAAXXX"],
      bankCodes: ["014"],
    },
    /* ---------------------------------
     * HUN
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "OTPVHUHBXXX",
      name: "OTP Bank",
      countryCode: "HUN",
      bics: ["OTPVHUHB", "OTPVHUHBXXX"],
      bankCodes: ["117"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "OKHBHUHBXXX",
      name: "K&H",
      countryCode: "HUN",
      bics: ["OKHBHUHBXXX", "OKHBHUHB"],
      bankCodes: ["104", "102"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "GIBAHUHBXXX",
      name: "Erste Bank",
      countryCode: "HUN",
      bics: ["GIBAHUHBXXX", "GIBAHUHB"],
      bankCodes: ["116", "119"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BACXHUHBXXX",
      name: "UniCredit Bank Hungary Zrt",
      countryCode: "HUN",
      bics: ["BACXHUHBXXX", "BACXHUHB"],
      bankCodes: ["109"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "UBRTHUHBXXX",
      name: "Raiffeisen",
      countryCode: "HUN",
      bics: ["UBRTHUHBXXX", "UBRTHUHB"],
      bankCodes: ["120"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CIBHHUHBXXX",
      name: "CIB",
      countryCode: "HUN",
      bics: ["CIBHHUHBXXX", "CIBHHUHB"],
      bankCodes: ["107", "111"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "GNBAHUHBXXX",
      name: "GRÁNIT Bank ZRT.",
      countryCode: "HUN",
      bics: ["GNBAHUHB", "GNBAHUHBXXX"],
      bankCodes: ["121"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HBWEHUHBXXX",
      name: "MagNet Bank",
      countryCode: "HUN",
      bics: ["HBWEHUHB", "HBWEHUHBXXX"],
      bankCodes: ["162", "176"],
    },
    /* ---------------------------------
     * ITA
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "BCITITMMXXX",
      name: "Intesa Sanpaolo InBiz",
      countryCode: "ITA",
      bics: ["BCITITMM", "BCITITMMXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "UNCRITMMXXX",
      name: "UniCredit S.p.A.",
      countryCode: "ITA",
      bics: ["UNCRITMM", "UNCRITMMXXX", "UNCRITM1", "UNCRITM1XXX"],
      bankCodes: ["02008"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BAPPIT22XXX",
      name: "Banco BPM Privati (YouWeb)",
      countryCode: "ITA",
      bics: ["BAPPIT22", "BAPPIT22XXX"],
      bankCodes: ["05034"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BPMOIT22XXX",
      name: "BPER Banca Privati",
      countryCode: "ITA",
      bics: ["BPMOIT22", "BPMOIT22XXX"],
      bankCodes: ["05387"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BPPIITRRXXX",
      name: "Poste Italiane S.p.A.- Patrimonio BancoPosta",
      countryCode: "ITA",
      bics: ["BPPIITRRXXX", "BPPIITRR"],
      bankCodes: ["07601"],
    },
    /* ---------------------------------
     * LVA
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "HABALV22XXX",
      name: "Swedbank",
      countryCode: "LVA",
      bics: ["HABALV22", "HABALV22XXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "UNLALV2XXXX",
      name: "SEB",
      countryCode: "LVA",
      bics: ["UNLALV2X", "UNLALV2XXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "PARXLV22XXX",
      name: "Citadele Bank",
      countryCode: "LVA",
      bics: ["PARXLV22", "PARXLV22XXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "RIKOLV2XXXX",
      name: "Luminor",
      countryCode: "LVA",
      bics: ["RIKOLV2X", "RIKOLV2XXXX"],
      bankCodes: [],
    },
    /* ---------------------------------
     * NLD
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "INGBNL2AXXX",
      name: "ING",
      countryCode: "NLD",
      bics: ["INGBNL2A", "INGBNL2AXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "RABONL2UXXX",
      name: "Rabobank",
      countryCode: "NLD",
      bics: ["RABONL2UXXX", "RABONL2U"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "ABNANL2AXXX",
      name: "ABN AMRO",
      countryCode: "NLD",
      bics: ["ABNANL2A", "ABNANL2AXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "RBRBNL21XXX",
      name: "ASN Bank vh RegioBank",
      countryCode: "NLD",
      bics: ["RBRBNL21XXX", "RBRBNL21"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BUNQNL2AXXX",
      name: "bunq",
      countryCode: "NLD",
      bics: ["BUNQNL2A", "BUNQNL2AXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "KNABNL2HXXX",
      name: "Knab",
      countryCode: "NLD",
      bics: ["KNABNL2HXXX", "KNABNL2H"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "TRIONL2UXXX",
      name: "Triodos",
      countryCode: "NLD",
      bics: ["TRIONL2UXXX", "TRIONL2U"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "ASNBNL21XXX",
      name: "ASN Bank",
      countryCode: "NLD",
      bics: ["ASNBNL21", "ASNBNL21XXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "FVLBNL22XXX",
      name: "Van Lanschot Kempen",
      countryCode: "NLD",
      bics: ["FVLBNL22XXX", "FVLBNL22"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "REVONL22XXX",
      name: "Revolut",
      countryCode: "NLD",
      bics: ["REVONL22", "REVONL22XXX"],
      bankCodes: ["70700", "32500"],
    },
    /* ---------------------------------
     * NOR
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "DNBANOKKXXX",
      name: "DNB",
      countryCode: "NOR",
      bics: [
        "DNBANOKKXXX",
        "DNBANOKK",
        "DNBAFIHX",
        "DNBADKKX",
        "DNBASESX",
        "DNBAGB2L",
        "DNBAFIHXXXX",
        "DNBADKKXXXX",
        "DNBASESXXXX",
        "DNBAGB2LXXX",
      ],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "NDEANOKKXXX",
      name: "Nordea",
      countryCode: "NOR",
      bics: ["NDEANOKK", "NDEANOKKXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HANDNOKKXXX",
      name: "Handelsbanken",
      countryCode: "NOR",
      bics: ["HANDNOKK", "HANDNOKKXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "DABANO22XXX",
      name: "Danske Bank",
      countryCode: "NOR",
      bics: ["DABANO22XXX", "DABANO22"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "STFBNOKKXXX",
      name: "Storebrand",
      countryCode: "NOR",
      bics: ["STFBNOKK", "STFBNOKKXXX"],
      bankCodes: ["9680"],
    },
    /* ---------------------------------
     * PRT
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "CGDIPTPLXXX",
      name: "Caixa Geral de Depósitos S.A",
      countryCode: "PRT",
      bics: ["CGDIPTPL", "CGDIPTPLXXX"],
      bankCodes: ["0035"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BCOMPTPLXXX",
      name: "Banco Comercial Português S.A",
      countryCode: "PRT",
      bics: ["BCOMPTPL", "BCOMPTPLXXX"],
      bankCodes: ["0033"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BESCPTPLXXX",
      name: "Novo Banco S.A",
      countryCode: "PRT",
      bics: ["BESCPTPL", "BESCPTPLXXX"],
      bankCodes: ["0007"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "TOTAPTPLXXX",
      name: "Banco Santander S.A",
      countryCode: "PRT",
      bics: ["TOTAPTPL", "TOTAPTPLXXX"],
      bankCodes: ["0018"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BBPIPTPLXXX",
      name: "Banco Português de Investimento S.A",
      countryCode: "PRT",
      bics: ["BBPIPTPL", "BBPIPTPLXXX"],
      bankCodes: ["0010"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "CCCMPTPLXXX",
      name: "Crédito Agrícola (Moey)",
      countryCode: "PRT",
      bics: ["CCCMPTPL", "CCCMPTPLXXX"],
      bankCodes: ["0045"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "MPIOPTPLXXX",
      name: "Banco Montepio",
      countryCode: "PRT",
      bics: ["MPIOPTPLXXX", "MPIOPTPL"],
      bankCodes: ["0036"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BKBKPTPLXXX",
      name: "Bankinter S.A",
      countryCode: "PRT",
      bics: ["BKBKPTPLXXX", "BKBKPTPL"],
      bankCodes: ["0269"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "ACTVPTPLXXX",
      name: "Banco Activobank S.A",
      countryCode: "PRT",
      bics: ["ACTVPTPLXXX", "ACTVPTPL"],
      bankCodes: ["0023"],
    },
    /* ---------------------------------
     * ROU
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "BTRLRO22XXX",
      name: "Banca Transilvania",
      countryCode: "ROU",
      bics: [
        "BTRLRO22",
        "BTRLRO22XXX",
        "BTRLRO21AB1",
        "BTRLRO21BBT",
        "BTRLRO21BH1",
        "BTRLRO21BH2",
        "BTRLRO21BH3",
        "BTRLRO21CJ1",
        "BTRLRO21",
        "BTRLRO21XXX",
      ],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "RNCBROBUXXX",
      name: "Banca Comerciala Romana",
      countryCode: "ROU",
      bics: ["RNCBROBU", "RNCBROBUXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BRDEROBUXXX",
      name: "BRD",
      countryCode: "ROU",
      bics: ["BRDEROBUXXX", "BRDEROBU"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "INGBROBUXXX",
      name: "ING",
      countryCode: "ROU",
      bics: ["INGBROBU", "INGBROBUXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "RZBRROBUXXX",
      name: "Raiffeisen",
      countryCode: "ROU",
      bics: ["RZBRROBU", "RZBRROBUXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BACXROBUXXX",
      name: "UniCredit Bank",
      countryCode: "ROU",
      bics: ["BACXROBU", "BACXROBUXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "REVOROBBXXX",
      name: "Revolut",
      countryCode: "ROU",
      bics: ["REVOROBB", "REVOROBBXXX"],
      bankCodes: ["70700", "32500"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BRELROBUXXX",
      name: "Libra Internet Bank",
      countryCode: "ROU",
      bics: ["BRELROBU", "BRELROBUXXX"],
      bankCodes: [],
    },
    /* ---------------------------------
     * SVN
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "LJBASI2XXXX",
      name: "NLB",
      countryCode: "SVN",
      bics: ["LJBASI2X", "LJBASI2XXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "KBMASI2XXXX",
      name: "OTP banka d.d.",
      countryCode: "SVN",
      bics: [
        "KBMASI2X",
        "KBMASI2XXXX",
        "ABANSI2X",
        "SKBASI2X",
        "ABANSI2XXXX",
        "SKBASI2XXXX",
      ],
      bankCodes: ["04", "01"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BAKOSI2XXXX",
      name: "Intesa Sanpaolo",
      countryCode: "SVN",
      bics: ["BAKOSI2X", "BAKOSI2XXXX"],
      bankCodes: ["10"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BACXSI22XXX",
      name: "UniCredit Bank",
      countryCode: "SVN",
      bics: ["BACXSI22", "BACXSI22XXX"],
      bankCodes: ["29"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HAABSI22XXX",
      name: "Addiko Bank",
      countryCode: "SVN",
      bics: ["HAABSI22", "HAABSI22XXX"],
      bankCodes: ["33"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "GORESI2XXXX",
      name: "Gorenjska Banka",
      countryCode: "SVN",
      bics: ["GORESI2X", "GORESI2XXXX"],
      bankCodes: ["07"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HDELSI22XXX",
      name: "Delavska Hranilnica",
      countryCode: "SVN",
      bics: ["HDELSI22", "HDELSI22XXX"],
      bankCodes: ["61"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "KSPKSI22XXX",
      name: "Sparkasse",
      countryCode: "SVN",
      bics: ["KSPKSI22", "KSPKSI22XXX"],
      bankCodes: ["34"],
    },
    /* ---------------------------------
     * ESP
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "CAIXESBBXXX",
      name: "CaixaBank",
      countryCode: "ESP",
      bics: [
        "CAIXESBB",
        "CAIXESBBXXX",
        "MIKBESB1XXX",
        "CAHMESMM",
        "MIKBESB1",
        "CAHMESMMXXX",
      ],
      bankCodes: ["2100", "0133", "2038"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BBVAESMMXXX",
      name: "BBVA",
      countryCode: "ESP",
      bics: ["BBVAESMM", "BBVAESMMXXX"],
      bankCodes: ["0182"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BSCHESMMXXX",
      name: "Santander",
      countryCode: "ESP",
      bics: [
        "BSCHESMM",
        "BSCHESMMXXX",
        "PSTRESMM",
        "POPIESMM",
        "NORTESMM",
        "ESPCESMM",
        "PSTRESMMXXX",
        "POPIESMMXXX",
        "NORTESMMXXX",
        "ESPCESMMXXX",
      ],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BSABESBBXXX",
      name: "Sabadell",
      countryCode: "ESP",
      bics: ["BSABESBB", "BSABESBBXXX"],
      bankCodes: ["0081"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "BKBKESMMXXX",
      name: "Bankinter",
      countryCode: "ESP",
      bics: ["BKBKESMM", "BKBKESMMXXX"],
      bankCodes: ["0128"],
    },
    /* ---------------------------------
     * SWE
     * --------------------------------- */
    {
      valueUsage: "CanBeEntered",
      value: "SWEDSESSXXX",
      name: "Swedbank",
      countryCode: "SWE",
      bics: ["SWEDSESS", "SWEDSESSXXX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "HANDSESSXXX",
      name: "Handelsbanken",
      countryCode: "SWE",
      bics: ["HANDSESSXXX", "HANDSESS"],
      bankCodes: ["600000000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "ESSESESSXXX",
      name: "SEB",
      countryCode: "SWE",
      bics: ["ESSESESSXXX", "ESSESESS"],
      bankCodes: ["500000000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "NDEASESSXXX",
      name: "Nordea",
      countryCode: "SWE",
      bics: ["NDEASESS", "NDEASESSXXX"],
      bankCodes: ["300000000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "DABASESXXXX",
      name: "Danske Bank",
      countryCode: "SWE",
      bics: ["DABASESXXXX", "DABASESX"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "ELLFSESSXXX",
      name: "Lansforsakringar Bank",
      countryCode: "SWE",
      bics: ["ELLFSESSXXX", "ELLFSESS"],
      bankCodes: [],
    },
    {
      valueUsage: "CanBeEntered",
      value: "SBAVSESSXXX",
      name: "SBAB Bank AB",
      countryCode: "SWE",
      bics: ["SBAVSESSXXX", "SBAVSESS"],
      bankCodes: ["925000000"],
    },
    {
      valueUsage: "CanBeEntered",
      value: "IBCASES1XXX",
      name: "ICA Banken",
      countryCode: "SWE",
      bics: ["IBCASES1XXX", "IBCASES1"],
      bankCodes: ["927000000"],
    },
  ],
} as const satisfies EnumDefinition<BankEnumItem>;
